// S2 paste box: textarea + live parse preview (/parse), used inline on the hub and as a sheet elsewhere.
import { html, attr } from './singles-hub--core--html.js?v=c7c57de095';
import { api } from './singles-hub--api.js?v=c7c57de095';
import { debounce } from './singles-hub--format.js?v=c7c57de095';
import { route } from './singles-hub--config.js?v=c7c57de095';
import { encodeList } from './singles-hub--format.js?v=c7c57de095';
import { dialog, icon } from './singles-hub--components--ui.js?v=c7c57de095';
import { toast } from './singles-hub--components--host.js?v=c7c57de095';

export const SAMPLE_LIST = `Pokémon: 20
4 Dreepy TWM 128
4 Drakloak TWM 129
3 Dragapult ex TWM 130
2 Budew ASC 16
2 Munkidori TWM 95
1 Dunsparce JTG 120
1 Dudunsparce TEF 129
1 Fezandipiti ex ASC 142
1 Meowth ex POR 62
1 Moltres PFL 14

Trainer: 32
4 Lillie's Determination MEG 119
3 Boss's Orders MEG 114
2 Crispin SCR 133
1 Rosa's Encouragement POR 84
1 Judge POR 76
4 Buddy-Buddy Poffin TEF 144
4 Poké Pad POR 81
4 Crushing Hammer POR 71
3 Night Stretcher ASC 196
3 Ultra Ball MEG 131
1 Unfair Stamp TWM 165
2 Risky Ruins MEG 127

Energy: 8
3 Psychic Energy MEE 13
3 Fire Energy MEE 10
2 Darkness Energy MEE 15

Total Cards: 60`;

const PH = 'Pokémon: 20\n4 Dreepy TWM 128\n4 Drakloak TWM 129\n…\n\nTrainer: 32\n4 Ultra Ball MEG 131\n…\n\nEnergy: 8\n3 Psychic Energy MEE 13';

export const pasteState = () => ({ pText: '', pParse: null, pBusy: false, pOff: false, pOpen: false });

export function summary(s) {
  const p = s.pParse;
  if (!s.pText.trim()) return { text: 'Paste a list to see it checked here.', cls: '', ok: false };
  if (s.pOff) return { text: 'Deck matching is offline right now, so we can’t check this list. Please try again shortly.', cls: 'mcc-parse--bad', ok: false };
  if (s.pBusy && !p) return { text: 'Checking your list…', cls: '', ok: false };
  if (!p) return { text: '', cls: '', ok: false };
  if (p.kind === 'limitless_link' || p.kind === 'link' || p.kind === 'too_long') return { text: p.warnings[0], cls: 'mcc-parse--bad', ok: false };
  if (p.kind === 'other_game') return { text: p.warnings[0] || 'These look like cards from another game. The deck builder only reads Pokémon lists for now.', cls: 'mcc-parse--bad', ok: false };
  const rec = p.lines.filter((l) => l.card_id).length;
  if (!rec) return { text: 'We couldn’t read any Pokémon cards. Each line should look like “4 Ultra Ball MEG 131” or “4 Ultra Ball”.', cls: 'mcc-parse--bad', ok: false };
  const clean = p.total === 60 && !p.warnings.length && !p.checks.length;
  return { text: `${p.kind === 'mass' ? 'TCGplayer codes · ' : ''}${p.total} cards · ${p.counts.pokemon} Pokémon · ${p.counts.trainer} Trainers · ${p.counts.energy} Energy${p.counts.unrecognised ? ` · ${p.counts.unrecognised} unrecognised` : ''}${clean ? ' ✓' : ''}`, cls: clean ? 'mcc-parse--ok' : '', ok: true };
}

// Feedback 2026-10-10: deck-size / copy-limit / rotation problems must not hide behind minor notes. Serious first,
// then the rest in list order; several notes about one line become one entry ("Line 4: … · …").
const SERIOUS = /a Standard deck is|max \d+ with the same name|more than a whole deck|negative number|not recognised|can't match|isn't Standard legal|ACE SPEC/i;
export function orderWarnings(warnings, checks) {
  const merged = []; const byLine = {};
  warnings.concat(checks).forEach((w) => {
    const m = /^Line (\d+)[:\s]\s*(.*)$/.exec(w);
    if (m && byLine[m[1]]) { const e = byLine[m[1]]; e.text += ' · ' + m[2]; e.serious = e.serious || SERIOUS.test(w); return; }
    const e = { text: w, serious: SERIOUS.test(w) };
    if (m) byLine[m[1]] = e;
    merged.push(e);
  });
  return merged.filter((e) => e.serious).concat(merged.filter((e) => !e.serious)).map((e) => e.text);
}

// cta / help: the hub's importer panel words its button and tip for first-time visitors (redesign 2026-10-18)
export function pasteBox(s, { idPrefix = 'hub', inline = true, cta = 'Build this deck', help = null } = {}) {
  const sm = summary(s);
  const warns = s.pParse && sm.ok ? orderWarnings(s.pParse.warnings, s.pParse.checks) : [];
  return html`<div class="mcc-paste">
    <label class="mcc-sr" for="mcc-${idPrefix}-paste">Deck list</label>
    <textarea id="mcc-${idPrefix}-paste" class="mcc-textarea" placeholder="${PH}" data-input="pasteInput" spellcheck="false" aria-describedby="mcc-${idPrefix}-parse" ${inline ? '' : 'data-autofocus'}>${s.pText}</textarea>
    <p id="mcc-${idPrefix}-parse" class="mcc-parse ${sm.cls}" aria-live="polite">${sm.text}</p>
    ${warns.length ? html`<ul class="mcc-parse-warn">${warns.slice(0, 3).map((w) => html`<li>${w}</li>`)}</ul>${warns.length > 3 ? html`<details class="mcc-more mcc-parse-warn"><summary>…and ${warns.length - 3} more</summary><ul>${warns.slice(3).map((w) => html`<li>${w}</li>`)}</ul></details>` : ''}` : ''}
    <div class="mcc-row" style="flex-wrap:nowrap">
      <button type="button" class="mcc-btn mcc-btn--lg" style="flex:1" data-click="pasteBuild"${attr('disabled', !sm.ok)}>${cta}</button>
      <button type="button" class="mcc-btn mcc-btn--secondary mcc-btn--lg" data-click="pasteClipboard" title="Paste from clipboard">${icon.clip}<span class="mcc-only-desk">Paste</span><span class="mcc-sr">Paste from clipboard</span></button>
    </div>
    <p class="mcc-xs mcc-muted">${help || 'From Limitless: on the list page press Copy to Clipboard. From PTCGL: Deck → Export. TCGplayer codes (4-550172||…) work too.'}</p>
  </div>`;
}

export function pasteSheet(s) {
  return dialog({ id: 'mcc-paste-dlg', title: 'Paste a deck list', body: html`<div class="mcc-row" style="justify-content:flex-end;margin-bottom:8px"><button type="button" class="mcc-link mcc-small" data-click="pasteSample">Use the sample list</button></div>${pasteBox(s, { idPrefix: 'dlg', inline: false })}` });
}

export function pasteActions({ onBuild }) {
  let seq = 0;
  const run = debounce(async (c) => {
    const my = ++seq; const t = c.state.pText;
    if (!t.trim()) { c.setState({ pParse: null, pBusy: false }); return; }
    try { const p = await api.parse(t); if (my === seq) c.setState({ pParse: p, pBusy: false, pOff: false }); }
    catch (e) { if (my === seq) c.setState({ pParse: null, pBusy: false, pOff: !!e.offline }); }
  }, 220);
  return {
    pasteInput: (el, e, c) => { c.state.pText = el.value; c.setState({ pBusy: true }); run(c); },
    pasteSample: (el, e, c) => { c.setState({ pText: SAMPLE_LIST, pBusy: true }); run(c); },
    pasteClear: (el, e, c) => c.setState({ pText: '', pParse: null }),
    pasteClipboard: async (el, e, c) => {
      try { const t = await navigator.clipboard.readText(); c.setState({ pText: t, pBusy: true }); run(c); }
      catch (err) { toast('Your browser blocked clipboard access. Paste with Ctrl/Cmd + V.'); }
    },
    pasteOpen: (el, e, c) => c.setState({ pOpen: true }),
    pasteBuild: (el, e, c) => { if (!summary(c.state).ok) return; onBuild(c.state.pText, c.state.pParse, c); },
  };
}

export function builderUrlForText(text, parsed) {
  if (parsed && parsed.kind === 'mass') return route('builderCodes', { codes: text.replace(/\s+/g, '') });
  return route('builderList', { list: encodeList(text) });
}
